import { useSelector } from 'react-redux'

import type { TAttentionStatusRecipient } from 'partner360/entities/attentionStatuses'

import type { IFinancialCockpitUserWithScope } from '../../../api/types'
import {
  selectCockpitManagerUsers,
  selectCockpitPartnerUsers,
  selectCockpitPbmUsers,
} from '../../../model/selectors'

import { useAttentionCardPartner } from './useAttentionCardPartner'

/** Names past this many are counted rather than listed — the row states who, not everyone. */
const NAMES_SHOWN = 2

const formatNames = (users: IFinancialCockpitUserWithScope[], partnerId?: string): string => {
  const names = [
    ...new Set(
      users
        .filter((user) => (partnerId ? user.PartnerId === partnerId : true))
        .map(({ FullName }) => FullName?.trim())
        .filter(Boolean)
    ),
  ]

  if (names.length === 0) {
    return ''
  }

  return names.length > NAMES_SHOWN
    ? `${names.slice(0, NAMES_SHOWN).join(', ')} +${names.length - NAMES_SHOWN}`
    : names.join(', ')
}

/**
 * Who a reminder of the opened status would reach, by recipient.
 *
 * The payload names the partner's people per offering, so the same offering the card stands on
 * decides the names — and in the manager cockpit the drilled-down partner narrows them further.
 * A recipient the payload names nobody for reads as an empty string: the control then offers the
 * act under its own name alone rather than under an invented one.
 */
export const useAttentionReminderRecipients = (): Record<TAttentionStatusRecipient, string> => {
  const { partnerId } = useAttentionCardPartner()
  const pbmUsers = useSelector(selectCockpitPbmUsers)
  const managerUsers = useSelector(selectCockpitManagerUsers)
  const partnerUsers = useSelector(selectCockpitPartnerUsers)

  return {
    pbm: formatNames(pbmUsers, partnerId),
    manager: formatNames(managerUsers, partnerId),
    partner: formatNames(partnerUsers, partnerId),
  }
}
